/**
 * message.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Todd L Smith <tsmith@safetyweb.org>
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Identifiable, Tagged } from "../../../support/types/aliases";
import { descriptor } from "../../../support/types/decorators";
import { MessageTag } from "../message-tag";
import { ResponseCode } from "./response-code";

export { Tagged } from "app/support/types/aliases";

export interface MessageMetadata {
  allowUnboundedReplies? : boolean;
  timeout? : number;
}

export interface OkMessage {
  code : ResponseCode;
  ok : boolean;
}

/**
 * A client sends and receives {@link Message}s.
 */
export abstract class Message
{
  /**
   * The {@link Message} tag.
   * @type {MessageTag}
   */
  public tag : MessageTag;

  /**
   * The {@link Message} identifier.
   * @type {number}
   */
  public id : number;

  /**
   * The {@link Message} that triggered this server reply, if applicable.
   * @type {[type]}
   */
  public request? : Message;

  /**
   * The time the {@link Message} was sent or received. This property is
   * generally only present in logging or debug tools.
   * @type {number}
   */
  public timestamp? : number;

  /**
   * Whether the {@link Message} was sent from the client to the server, as
   * opposed to incoming messages from the server. This property is generally
   * only present in logging or debug tools.
   * @type {[type]}
   */
  public outgoing? : boolean;

  /**
   * The response code indicating success or different generic failure reasons.
   * This is predominantly a property of {@link AcknowledgementInfoMessage}, but
   * appears across a few other messages as well and is useful to reserve as an
   * optional global property of all message types.
   */
  public code? : ResponseCode;

  @descriptor({enumerable: false, value: {}})
  public meta? : MessageMetadata;

  /**
   * Does the {@link Message} expect a reply?
   *
   * @return
   *        true if the {@link Message} expects a reply, false otherwise.
   */
  public abstract expectsReply () : boolean;

  // TODO: enable
  // public abstract sendable () : boolean;

  /**
   * Does the tag of a {@link Message} match a given {@link Tagged} item?
   */
  public static isA (message : Message, compare : Tagged | number) : boolean
  {
    const tag : number = (typeof compare === "number") ? compare : compare.tag;
    return (message.tag === tag);
  }

  /**
   * Does this {@link Message}'s tag match a given {@link Tagged} item?
   */
  public isA (compare : Tagged) : boolean
  {
    return (this.tag === compare.tag);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/messages/general/message.ts